import { Icon } from "@iconify/react";
import React, { useEffect, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Toast } from "primereact/toast";
import { Button } from "primereact/button";
import { RootState, AppDispatch } from "../../redux/store";
import {
  markAsRead,
  removeNotification,
  NotificationItem,
} from "../../redux/slices/notificationSlice";
import { notificationService } from "../../services/notificationService";
import { formatDate } from "../../utils/functions";

const ADMIN_ROLES = ["ADMIN", "SUPER_ADMIN", "REVIEWER"];

export const NotificationButton = () => {
  const toastRef = useRef<Toast>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const dispatch = useDispatch<AppDispatch>();

  const { currentUser } = useSelector((state: RootState) => state.auth);
  const { items: notifications } = useSelector((state: RootState) => state.notifications);

  const [isOpen, setIsOpen] = useState(false);
  const [showAll, setShowAll] = useState(false);
  const [selected, setSelected] = useState<NotificationItem | null>(null);
  const [showModal, setShowModal] = useState(false);

  const unreadCount = notifications.filter((n) => !n.isRead).length;
  const visible = showAll ? notifications : notifications.slice(0, 3);

  // Fermer au clic extérieur
  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target as Node)) {
        setIsOpen(false);
        setShowAll(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const handleNotificationClick = async (notif: NotificationItem) => {
    try {
      if (!notif.isRead) {
        await notificationService.readNotification(notif.id);
        dispatch(markAsRead(notif.id));
      }
      setSelected(notif);
      setShowModal(true);
    } catch {
      toastRef.current?.show({
        severity: "error",
        summary: "Error",
        detail: "Failed to mark notification as read.",
      });
    }
  };

  const handleDelete = async (e: React.MouseEvent, id: string) => {
    e.stopPropagation();
    e.preventDefault();
    try {
      await notificationService.deleteNotification(id);
      dispatch(removeNotification(id));
    } catch {
      toastRef.current?.show({
        severity: "error",
        summary: "Error",
        detail: "Failed to delete notification.",
      });
    }
  };

  const isAdmin = ADMIN_ROLES.includes(currentUser?.role?.role_code ?? "");

  return (
    <div className="relative" ref={dropdownRef}>
      <Toast ref={toastRef} />

      <button
        onClick={() => setIsOpen((prev) => !prev)}
        className="has-indicator w-12 h-12 rounded-full flex justify-center items-center"
        type="button"
      >
        <Icon icon="iconoir:bell" className="text-primary-light text-lg translate-x-2/3" />
        {unreadCount > 0 && (
          <span className="absolute top-2 right-0 translate-x-1/2 translate-y-1/3 bg-red-500 text-white text-[8px] font-bold w-3 h-3 flex items-center justify-center rounded-full">
            {unreadCount > 9 ? "9+" : unreadCount}
          </span>
        )}
      </button>

      {isOpen && (
        <div className="absolute right-0 mt-2 w-96 bg-white border rounded-lg shadow-lg z-50 overflow-hidden">
          <div className="p-1 flex justify-between items-center bg-primary-50">
            <p className="text-xs text-primary-light font-semibold pl-2 mb-0">
              {isAdmin ? "Admin Notifications" : "Notifications"}
            </p>
            <span className="text-primary-600 font-semibold text-xs w-8 h-8 rounded-full bg-base flex justify-center items-center">
              {notifications.length}
            </span>
          </div>

          <div className="max-h-72 overflow-y-auto p-4 space-y-2">
            {visible.length === 0 ? (
              <p className="text-xs text-gray-400 text-center py-4">No notifications</p>
            ) : (
              visible.map((notif) => (
                <a
                  key={notif.id}
                  href="#"
                  onClick={(e) => { e.preventDefault(); handleNotificationClick(notif); }}
                  className={`flex items-center gap-3 p-3 hover:bg-gray-100 rounded-lg ${
                    !notif.isRead ? "bg-secondary-100" : ""
                  }`}
                >
                  <div className="flex-1 overflow-hidden">
                    <p
                      className="text-xs text-gray-500"
                      style={{ whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis", maxWidth: "180px" }}
                    >
                      {notif.message}
                    </p>
                  </div>
                  <span className="text-xs text-gray-400 flex-shrink-0">{formatDate(notif.createdAt)}</span>
                  <Button
                    className="text-red-500 hover:text-red-700 text-sm"
                    icon="pi pi-trash"
                    onClick={(e) => handleDelete(e, notif.id)}
                    title="Delete"
                  />
                </a>
              ))
            )}
          </div>

          <div className="text-center py-4 border-t">
            <button
              className="text-primary-600 font-semibold text-xs hover:text-primary-500"
              onClick={() => setShowAll((prev) => !prev)}
            >
              {showAll ? "Show Less" : "See All Notifications"}
            </button>
          </div>
        </div>
      )}

      {showModal && selected && (
        <div className="fixed inset-0 z-50 bg-black bg-opacity-30 flex items-center justify-center">
          <div className="bg-white rounded-lg shadow-xl w-full max-w-md p-6 relative">
            <button
              onClick={() => setShowModal(false)}
              className="absolute top-2 right-2 text-gray-500 hover:text-gray-700 text-lg"
            >
              &times;
            </button>
            <h2 className="text-sm font-semibold text-gray-800 mb-3">Notification</h2>
            <p className="text-sm text-gray-700 mb-4">{selected.message}</p>
            <p className="text-xs text-gray-400">Received {formatDate(selected.createdAt)}</p>
          </div>
        </div>
      )}
    </div>
  );
};
